import type { CSSProperties, FC, ReactElement } from 'react';

import type { IGridCell } from './GridCell';

import testId from '@snowit/utils/testId';

import GridCell from './GridCell';

import cx from 'classnames';
import s from './Grid.module.scss';

export interface IGrid {
  cells: IGridCell[];
  columns: number;
  size?: (string | number)[];
  gap?: number;
  id?: string;
  className?: string;
  test?: string;
}

const getCellStartAndSpan = (
  cell: IGridCell,
  i: number,
  cells: IGridCell[],
  columns: number,
): { start?: number; span?: number } => {
  if (cell.columnStart && cell.columnSpan) {
    return {
      start: cell.columnStart,
      span: cell.columnSpan,
    };
  }
  const cellSize = cell.size ?? columns;
  const prev = cells.reduce((memo, c, k) => {
    if (k >= i) return memo;
    const size = c.size ?? columns;
    if (size === columns) return 0;
    return (memo + size) % columns;
  }, 0);

  return {
    start: prev + cellSize > columns ? 1 : prev + 1,
    span: cellSize,
  };
};

const Grid: FC<IGrid> = (props: IGrid): ReactElement => {
  const { cells, columns, gap, size = [], id, className, test } = props;

  const sizes = Array.isArray(size) ? size : Array(columns).fill(size);

  const cssColumns = Array(columns)
    .fill(0)
    .map((_, col) => {
      const sz = sizes[col];
      if (!sz) return '1fr';
      if (typeof sz === 'number') return `${sz}fr`;
      return sz;
    })
    .join(' ');

  const renderCells = () => {
    if (!cells?.length) return null;
    return cells.map((cell, i, self) => {
      const { start, span } = getCellStartAndSpan(cell, i, self, columns);
      return (
        <GridCell
          key={cell.key}
          id={cell.key}
          cell={{ ...cell, columnSpan: span, columnStart: start }}
          test={test}
        />
      );
    });
  };

  return (
    <div
      id={id}
      className={cx(s.grid, className)}
      {...testId(test)}
      style={
        {
          '--columns': cssColumns,
          '--gap': gap,
        } as CSSProperties
      }
    >
      {renderCells()}
    </div>
  );
};

export default Grid;
